<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { storeToRefs } from 'pinia';
import { trans } from '../../../_00_elements/translation';
import useManagePayAccount from '../store/managePayAccount';
import { useLoading } from '../../../_00_elements/tools/composables/useLoading';
import Typography from '../../../_01_atoms/components/Typography.vue';
import Button from '../../../_01_atoms/components/Button.vue';
import { getPayAccountValidator } from '../store/validators/payAccountValidator';
import PhoneNumber from '../../../_00_elements/tools/phoneNumber';
import PhoneInput from '../../../_01_atoms/components/PhoneInput.vue';

const { isLoading, startLoading, stopLoading } = useLoading();
const managePayAccount = useManagePayAccount();

managePayAccount.setPayOrganizationBillingInformation({
  ...window.initialState.payOrganizationBillingInformation,
  company_name: window.initialState.organization?.name,
});

managePayAccount.setBillingMode(
  window.initialState.organizationType,
  window.initialState.billingType,
);

const { payOrganizationBillingInformation } = storeToRefs(managePayAccount);

const isValidPhone = ref(true);
const isPhoneTouched = ref(false);

const phoneNumber = computed({
  get: () => new PhoneNumber(payOrganizationBillingInformation.value.phone_number ?? ''),
  set: (val) => {
    payOrganizationBillingInformation.value.phone_number = val.international;
    isPhoneTouched.value = true;
    isValidPhone.value = val.isValid || !val;
  },
});

const rules = computed(() => getPayAccountValidator());
const v$ = useVuelidate(rules, payOrganizationBillingInformation.value);

const { calculateVATNumber } = managePayAccount;

const onSirenBlur = () => {
  v$.value.siren.$touch();
  if (!v$.value.siren.$invalid) {
    payOrganizationBillingInformation.value.vat_number = calculateVATNumber(payOrganizationBillingInformation.value.siren);
  }
};

const isNextDisabled = computed(() => v$.value.$invalid || !isValidPhone.value);

const handleClickValidate = async () => {
  v$.value.$touch();
  if (isNextDisabled.value) {
    return;
  }

  startLoading();
  if (!payOrganizationBillingInformation.value.phone_number || payOrganizationBillingInformation.value.phone_number === '+') {
    payOrganizationBillingInformation.value.phone_number = null;
  }

  await managePayAccount.createOrUpdatePayOrganizationClientBillingInformation();
  stopLoading();
};
</script>

<template>
  <div class="manage">
    <h1 class="manage__title">
      {{ trans('gestion.pay.informations.title') }}
    </h1>
    <div class="manage__form">
      <div v-for="field in [ 'company_name', 'legal_name', 'street_and_number', 'additional_info' ]" :key="field" class="manage__form-group">
        <label class="manage__label">{{ trans(`gestion.pay.informations.${field}`) }}</label>
        <input
          v-model="payOrganizationBillingInformation[field]"
          type="text"
          class="manage__input"
          :class="{ error: v$[field].$error }"
          @blur="v$[field].$touch()"
        >
        <span v-if="v$[field].$error" class="error-message">{{ v$[field].$errors[0].$message }}</span>
      </div>

      <div class="manage__form-group-row">
        <div class="manage__form-group">
          <label class="manage__label">{{ trans('gestion.pay.informations.zip_code') }}</label>
          <input
            v-model="payOrganizationBillingInformation.zip_code"
            type="text"
            class="manage__input"
            :class="{ error: v$.zip_code.$error }"
            @blur="v$.zip_code.$touch()"
          >
          <span v-if="v$.zip_code.$error" class="error-message">{{ v$.zip_code.$errors[0].$message }}</span>
        </div>
        <div class="manage__form-group">
          <label class="manage__label">{{ trans('gestion.pay.informations.city') }}</label>
          <input
            v-model="payOrganizationBillingInformation.city"
            type="text"
            class="manage__input"
            :class="{ error: v$.city.$error }"
            @blur="v$.city.$touch()"
          >
          <span v-if="v$.city.$error" class="error-message">{{ v$.city.$errors[0].$message }}</span>
        </div>
      </div>

      <div v-for="field in [ 'siren', 'vat_number', 'email' ]" :key="field" class="manage__form-group">
        <label class="manage__label">{{ trans(`gestion.pay.informations.${field}`) }}</label>
        <input
          v-model="payOrganizationBillingInformation[field]"
          :type="field === 'email' ? 'email' : 'text'"
          class="manage__input"
          :class="{ error: v$[field].$error }"
          @blur="field === 'siren' ? onSirenBlur() : v$[field].$touch()"
        >
        <span v-if="v$[field].$error" class="error-message">{{ v$[field].$errors[0].$message }}</span>
      </div>

      <div class="manage__form-group">
        <label class="manage__label">{{ trans('gestion.pay.informations.phone') }}</label>
        <PhoneInput
          v-model="phoneNumber"
          class="manage__input"
          :class="{ error: !isValidPhone && isPhoneTouched }"
        />
        <span v-if="!isValidPhone && isPhoneTouched" class="error-message">{{ trans('gestion.pay.informations.phone.invalid') }}</span>
      </div>
    </div>

    <Typography variant="body-small" color="gray-dark">
      {{ trans('gestion.subscription.general.mandatory_fields') }}
    </Typography>
    <Button
      class="btn--small btn--primary manage__button"
      :class="{ 'loading-btn': isLoading }"
      :disabled="isNextDisabled || isLoading"
      @click="handleClickValidate"
    >
      {{ trans('global.register') }}
    </Button>
  </div>
</template>
